/**
 * Map General MIDI program numbers (and soft name hints) onto factory synth presets.
 * Piano (0–7) stays SF2 in the importer; this module covers pitched synth fallbacks.
 */
import type { SynthPreset } from "../types";
import { CLASSICAL_PRESETS } from "../kernel/classicalPresets";
import { clonePreset, DEFAULT_PRESETS } from "../kernel/defaultPresets";
import { ELECTRONIC_PRESETS } from "../kernel/electronicPresets";
import { KEYGEN_PRESETS } from "../kernel/keygenPresets";
import { MODULATION_PRESETS } from "../kernel/modulationPresets";

const FACTORY_BY_NAME: Map<string, SynthPreset> = (() => {
  const map = new Map<string, SynthPreset>();
  for (const preset of [
    ...DEFAULT_PRESETS,
    ...KEYGEN_PRESETS,
    ...ELECTRONIC_PRESETS,
    ...CLASSICAL_PRESETS,
    ...MODULATION_PRESETS,
  ]) {
    if (!map.has(preset.name)) {
      map.set(preset.name, preset);
    }
  }
  return map;
})();

function take(name: string): SynthPreset {
  const preset = FACTORY_BY_NAME.get(name);
  if (!preset) {
    throw new Error(`GM preset map references missing factory preset “${name}”`);
  }
  return clonePreset(preset);
}

/**
 * GM program (0–127) → factory preset name.
 * Tuned for “closest useful lab voice”, not 1:1 GM fidelity.
 */
const GM_PROGRAM_PRESET_NAME: readonly string[] = (() => {
  const names = Array.from({ length: 128 }, () => "Pad");

  const fill = (from: number, to: number, name: string): void => {
    for (let i = from; i <= to; i++) {
      names[i] = name;
    }
  };

  // 0–7 Piano — importer prefers SF2; map kept as soft fallback.
  fill(0, 7, "Airy Keys");
  // 8–15 Chromatic percussion
  names[8] = "Ivory Music Box"; // Celesta
  names[9] = "Glass Bell"; // Glockenspiel
  names[10] = "Ivory Music Box"; // Music Box
  names[11] = "Glass Bell"; // Vibraphone
  names[12] = "Temple Bell"; // Marimba
  names[13] = "Temple Bell"; // Xylophone
  names[14] = "Temple Bell"; // Tubular Bells
  names[15] = "Wire Harp"; // Dulcimer
  // 16–23 Organ
  fill(16, 18, "Basement Organ");
  fill(19, 20, "Chapel Positif");
  names[21] = "Basement Organ"; // Accordion
  names[22] = "Basement Organ"; // Harmonica
  names[23] = "Basement Organ"; // Tango Accordion
  // 24–31 Guitar — importer prefers ElectricGuitar / DistortionGuitar SF2;
  // these remain soft synth fallbacks if something stays on the synth path.
  fill(24, 28, "Neon Rain Pluck");
  fill(29, 30, "Neon Rain Pluck");
  names[31] = "Neon Rain Pluck"; // Guitar harmonics
  // 32–39 Bass
  fill(32, 35, "Bass");
  names[36] = "Warm Pluck Bass"; // Slap Bass 1
  names[37] = "Warm Pluck Bass"; // Slap Bass 2
  names[38] = "Acid Serpent"; // Synth Bass 1
  names[39] = "Reese Undertow"; // Synth Bass 2
  // 40–47 Solo strings
  names[40] = "Baroque Fiddle"; // Violin
  names[41] = "Baroque Fiddle"; // Viola
  names[42] = "Amber Cello"; // Cello
  names[43] = "Amber Cello"; // Contrabass
  names[44] = "Strings"; // Tremolo Strings
  names[45] = "Wire Harp"; // Pizzicato Strings
  names[46] = "Wire Harp"; // Orchestral Harp
  names[47] = "Cavern Tom"; // Timpani
  // 48–55 Ensemble
  fill(48, 49, "Strings"); // String Ensemble 1/2
  fill(50, 51, "Strings"); // Synth Strings 1/2
  names[52] = "Soft Choir"; // Choir Aahs
  names[53] = "Tracker Choir"; // Voice Oohs
  names[54] = "Tracker Choir"; // Synth Voice
  names[55] = "Orbital Brass"; // Orchestra Hit
  // 56–63 Brass
  fill(56, 59, "Gilded Clarion");
  fill(60, 61, "Orbital Brass"); // French Horn / Brass Section
  names[62] = "Orbital Brass"; // Synth Brass 1
  names[63] = "Hoover Riot"; // Synth Brass 2
  // 64–71 Reed
  names[64] = "Nasal Hautbois"; // Soprano Sax
  names[65] = "Nasal Hautbois"; // Alto Sax
  names[66] = "Nasal Hautbois"; // Tenor Sax
  names[67] = "Nasal Hautbois"; // Baritone Sax
  names[68] = "Nasal Hautbois"; // Oboe
  names[69] = "Nasal Hautbois"; // English Horn
  names[70] = "Nasal Hautbois"; // Bassoon
  names[71] = "Gilded Clarion"; // Clarinet
  // 72–79 Pipe
  fill(72, 74, "Silver Traverso"); // Piccolo / Flute / Recorder
  names[75] = "Silver Traverso"; // Pan Flute
  names[76] = "Glass Bell"; // Blown Bottle
  names[77] = "Silver Traverso"; // Shakuhachi
  names[78] = "Silver Traverso"; // Whistle
  names[79] = "Silver Traverso"; // Ocarina
  // 80–87 Synth Lead
  names[80] = "Lead"; // Square
  names[81] = "Iceberg Lead"; // Saw
  names[82] = "Chirp Arp"; // Calliope
  names[83] = "Chirp Arp"; // Chiff
  names[84] = "Pulse Lead"; // Charang
  names[85] = "Tracker Choir"; // Voice
  names[86] = "Pulse Lead"; // Fifths
  names[87] = "Iceberg Lead"; // Bass + Lead
  // 88–95 Synth Pad
  names[88] = "Pad"; // New Age
  names[89] = "Nostalgia Pad"; // Warm
  names[90] = "Aurora Drift"; // PolySynth
  names[91] = "Soft Choir"; // Choir
  names[92] = "Pad"; // Bowed
  names[93] = "Aurora Drift"; // Metallic
  names[94] = "Nostalgia Pad"; // Halo
  names[95] = "Chrome Supersaw"; // Sweep
  // 96–103 Synth FX
  fill(96, 99, "Neon Abyss");
  fill(100, 103, "Aurora Drift");
  // 104–111 Ethnic
  names[104] = "Wire Harp"; // Sitar
  names[105] = "Warm Pluck Bass"; // Banjo
  names[106] = "Wire Harp"; // Shamisen
  names[107] = "Wire Harp"; // Koto
  names[108] = "Temple Bell"; // Kalimba
  names[109] = "Basement Organ"; // Bagpipe
  names[110] = "Baroque Fiddle"; // Fiddle
  names[111] = "Nasal Hautbois"; // Shanai
  // 112–119 Percussive
  names[112] = "Temple Bell"; // Tinkle Bell
  names[113] = "Tick Hat"; // Agogo
  names[114] = "Paper Crane"; // Steel Drums
  names[115] = "Tick Hat"; // Woodblock
  names[116] = "Cavern Tom"; // Taiko
  names[117] = "Cavern Tom"; // Melodic Tom
  names[118] = "Chip Bell"; // Synth Drum
  names[119] = "Tick Hat"; // Reverse Cymbal
  // 120–127 SFX
  fill(120, 127, "Pad");

  return names;
})();

type NameRule = { re: RegExp; preset: string };

/** Soft overrides when the SMF name is more trustworthy than program 0. */
const NAME_HINTS: readonly NameRule[] = [
  { re: /string\s*ensemble|synth\s*string|orchestral\s*string|\bstrings?\b/i, preset: "Strings" },
  { re: /\bviolin\b|\bviola\b|\bfiddle\b/i, preset: "Baroque Fiddle" },
  { re: /\bcello\b|\bcontrabass\b|\bdouble\s*bass\b/i, preset: "Amber Cello" },
  { re: /\bchoir\b|\bahhs?\b|\boohs?\b|\bvoice\b|\bvocal/i, preset: "Soft Choir" },
  { re: /\bbrass\b|\btrumpet\b|\btrombone\b|\btuba\b|\bhorn\b/i, preset: "Orbital Brass" },
  { re: /\bsax\b|\boboe\b|\bbassoon\b|\bclarinet\b|\breed\b/i, preset: "Nasal Hautbois" },
  { re: /\bflute\b|\bpiccolo\b|\brecorder\b|\bpan\s*flute\b/i, preset: "Silver Traverso" },
  { re: /\borgan\b|\bchurch\b|\bpositif\b|\baccordion\b/i, preset: "Chapel Positif" },
  // Guitar name hints rarely apply — import classifies guitars as SF2 first.
  { re: /\bguitar\b|\bnylon\b|\bsteel\s*gt\b|\bacoustic\s*gt/i, preset: "Neon Rain Pluck" },
  { re: /\breese\b|\bsynth\s*bass\b/i, preset: "Reese Undertow" },
  { re: /\bbass\b|\bfingered\b|\bpicked\s*bass\b/i, preset: "Bass" },
  { re: /\blead\b|\bsquare\s*lead\b|\bsaw\s*lead\b/i, preset: "Lead" },
  { re: /\bpad\b|\batmos\b|\bwarm\s*pad\b/i, preset: "Pad" },
  { re: /\barpegg|\barp\b/i, preset: "Arp" },
  { re: /\bbell\b|\bglock\b|\bcelesta\b|\bmusic\s*box\b/i, preset: "Glass Bell" },
  { re: /\bharp\b|\bpizz/i, preset: "Wire Harp" },
  { re: /\bepiano\b|e-piano|electric\s*piano|\brodes\b/i, preset: "Airy Keys" },
];

/**
 * Resolve a GM program (+ optional track name) to a cloned factory SynthPreset.
 */
export function presetForGmProgram(
  program: number,
  nameHint?: string,
): SynthPreset {
  const hint = nameHint?.trim() ?? "";
  // Name can override a generic/missing program (common in loose MIDI files).
  if (hint) {
    for (const rule of NAME_HINTS) {
      if (rule.re.test(hint)) {
        return take(rule.preset);
      }
    }
  }

  const index = Number.isFinite(program)
    ? Math.max(0, Math.min(127, Math.round(program)))
    : 88;
  const presetName = GM_PROGRAM_PRESET_NAME[index] ?? "Pad";
  return take(presetName);
}

/** Exported for tests / docs — factory preset name for a GM program. */
export function gmProgramPresetName(program: number): string {
  const index = Math.max(0, Math.min(127, Math.round(program)));
  return GM_PROGRAM_PRESET_NAME[index] ?? "Pad";
}

/** Acoustic Grand Piano — fallback for unmapped / edited synth presets. */
export const GM_PIANO_PROGRAM = 0;

/**
 * Preferred GM program when a factory preset name maps to several programs.
 * Keeps export closer to the “primary” instrument in each family.
 */
const PREFERRED_GM_FOR_PRESET: Readonly<Record<string, number>> = {
  "Airy Keys": 4, // Electric Piano 1 (closer than Acoustic Grand for our soft keys)
  "Ivory Music Box": 10,
  "Glass Bell": 9,
  "Temple Bell": 14,
  "Wire Harp": 46,
  "Basement Organ": 16,
  "Chapel Positif": 19,
  "Neon Rain Pluck": 24,
  "Warm Pluck Bass": 36,
  Bass: 33,
  "Acid Serpent": 38,
  "Reese Undertow": 39,
  "Baroque Fiddle": 40,
  "Amber Cello": 42,
  Strings: 48,
  "Soft Choir": 52,
  "Celestial Chorale": 52,
  "Tracker Choir": 53,
  "Orbital Brass": 61,
  "Gilded Clarion": 56,
  "Hoover Riot": 63,
  "Nasal Hautbois": 68,
  "Silver Traverso": 73,
  Lead: 80,
  "Iceberg Lead": 81,
  "Chirp Arp": 82,
  "Pulse Lead": 84,
  Pad: 88,
  "Nostalgia Pad": 89,
  "Aurora Drift": 90,
  "Chrome Supersaw": 95,
  "Neon Abyss": 96,
  "Cavern Tom": 117,
  "Tick Hat": 115,
  "Paper Crane": 114,
  "Chip Bell": 118,
  Arp: 82,
  Kick: 0, // percussion-as-synth → piano; real drums use channel 10
  Hat: 0,
  Snare: 0,
};

const PRESET_TO_GM: Map<string, number> = (() => {
  const map = new Map<string, number>();
  for (let program = 0; program < 128; program++) {
    const name = GM_PROGRAM_PRESET_NAME[program]!;
    if (!map.has(name)) {
      map.set(name, program);
    }
  }
  for (const [name, program] of Object.entries(PREFERRED_GM_FOR_PRESET)) {
    if (FACTORY_BY_NAME.has(name)) {
      map.set(name, program);
    }
  }
  return map;
})();

function presetFingerprint(preset: SynthPreset): string {
  // Name excluded — callers compare name separately; this catches param edits.
  const { name: _name, ...rest } = preset;
  return JSON.stringify(rest);
}

/**
 * Reverse of `presetForGmProgram`: factory preset name (+ unmodified params) → GM program.
 * Edited or unknown presets fall back to Acoustic Grand Piano.
 */
export function gmProgramForPreset(
  preset: SynthPreset | undefined,
): { program: number; fallback: boolean } {
  if (!preset || typeof preset.name !== "string" || !preset.name.trim()) {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  const name = preset.name.trim();
  const program = PRESET_TO_GM.get(name);
  if (program === undefined) {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  const factory = FACTORY_BY_NAME.get(name);
  if (!factory) {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  if (presetFingerprint(preset) !== presetFingerprint(factory)) {
    return { program: GM_PIANO_PROGRAM, fallback: true };
  }
  return { program, fallback: false };
}
